const ATTRACTION_THRESHOLD = 0.6
const STRONG_LINK_THRESHOLD = 0.72
const MAX_LINKS = 160

export default class GenomeEngine {
  constructor() {
    this.links = []
    this.metrics = {
      averageDistance: 0
    }
    this.fieldState = {
      forcesX: new Float32Array(0),
      forcesY: new Float32Array(0)
    }
  }

  update(vibes, controls) {
    const count = vibes.length

    this.ensureFieldCapacity(count)
    this.fieldState.forcesX.fill(0, 0, count)
    this.fieldState.forcesY.fill(0, 0, count)

    const links = []
    let pairCount = 0
    let totalDistance = 0

    for (let i = 0; i < count; i += 1) {
      const a = vibes[i]

      for (let j = i + 1; j < count; j += 1) {
        const b = vibes[j]
        const similarity = this.calculateSimilarity(a.axes, b.axes)
        const emotionalDistance = 1 - similarity
        const symbolicDistance = 86 + emotionalDistance * 190
        const dx = b.x - a.x
        const dy = b.y - a.y
        const distance = Math.hypot(dx, dy) || 0.0001
        const directionX = dx / distance
        const directionY = dy / distance

        pairCount += 1
        totalDistance += emotionalDistance

        this.applyAttraction({
          i,
          j,
          similarity,
          distance,
          symbolicDistance,
          directionX,
          directionY,
          controls
        })

        this.applyRepulsion({
          i,
          j,
          similarity,
          distance,
          symbolicDistance,
          directionX,
          directionY,
          controls
        })

        if (similarity >= STRONG_LINK_THRESHOLD) {
          links.push({
            a,
            b,
            strength: similarity,
            emotionalDistance,
            symbolicDistance
          })
        }
      }
    }

    this.links = links
      .sort((left, right) => right.strength - left.strength)
      .slice(0, MAX_LINKS)

    this.metrics.averageDistance = pairCount === 0 ? 0 : totalDistance / pairCount
  }

  getLinks() {
    return this.links
  }

  getFieldState() {
    return this.fieldState
  }

  getMetrics() {
    return this.metrics
  }

  ensureFieldCapacity(count) {
    if (this.fieldState.forcesX.length === count) {
      return
    }

    this.fieldState.forcesX = new Float32Array(count)
    this.fieldState.forcesY = new Float32Array(count)
  }

  applyAttraction({ i, j, similarity, distance, symbolicDistance, directionX, directionY, controls }) {
    if (similarity < ATTRACTION_THRESHOLD) {
      return
    }

    const affinity = (similarity - ATTRACTION_THRESHOLD) / (1 - ATTRACTION_THRESHOLD)
    const stretch = distance - symbolicDistance
    const strength = stretch * 0.0016 * affinity * controls.gravityStrength

    this.fieldState.forcesX[i] += directionX * strength
    this.fieldState.forcesY[i] += directionY * strength
    this.fieldState.forcesX[j] -= directionX * strength
    this.fieldState.forcesY[j] -= directionY * strength
  }

  applyRepulsion({ i, j, similarity, distance, symbolicDistance, directionX, directionY, controls }) {
    const incompatibility = similarity < ATTRACTION_THRESHOLD
      ? (ATTRACTION_THRESHOLD - similarity) / ATTRACTION_THRESHOLD
      : 0

    const territorialRadius = symbolicDistance * 0.78
    let strength = 0

    if (distance < territorialRadius) {
      strength += (territorialRadius - distance) * 0.0018 * (0.35 + incompatibility) * controls.repulsionStrength
    }

    const personalSpace = 28 + (1 - similarity) * 16

    if (distance < personalSpace) {
      strength += (personalSpace - distance) * 0.0032 * (0.25 + controls.clusterStabilization)
    }

    if (strength === 0) {
      return
    }

    this.fieldState.forcesX[i] -= directionX * strength
    this.fieldState.forcesY[i] -= directionY * strength
    this.fieldState.forcesX[j] += directionX * strength
    this.fieldState.forcesY[j] += directionY * strength
  }

  calculateSimilarity(a, b) {
    const axisKeys = Object.keys(a)

    if (axisKeys.length === 0) {
      return 0
    }

    let sum = 0

    axisKeys.forEach(key => {
      sum += Math.pow((a[key] || 0) - (b[key] || 0), 2)
    })

    const maxDistance = Math.sqrt(axisKeys.length * 100 * 100)
    const distance = Math.sqrt(sum)

    return Math.max(0, 1 - distance / maxDistance)
  }
}
